'use strict';

var React = require('react');
var reactIcons = require('@sravni/react-icons');
var reactUtils = require('@sravni/react-utils');
var Alert = require('../../Alert/src/Alert.js');
var Button = require('../../Button/src/Button.js');
var Icon = require('../../Icon/src/Icon.js');
var Space = require('../../Space/src/Space.js');
require('../../Typography/src/Heading.js');
require('../../Typography/src/Link.js');
require('../../Typography/src/ListItem.js');
require('../../Typography/src/OrderedList.js');
var Text = require('../../Typography/src/Text.js');
require('../../Typography/src/UnorderedList.js');
var Uploader_module = require('../styles/Uploader.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/**
 * @description Uploader component.
 *
 * @component
 * @example
 * ```jsx
 * <Uploader title="title">
 *   <Uploader.Item key={fileKey} file={file}/>
 * </Uploader>
 * ```
 */
const Uploader = React__namespace.forwardRef((props, ref) => {
    const { title, description, dragZoneDescription, dragZonePreview, buttonCaption, multiple, accept, disabled, className, children, onChange, onFilesUpload, onDragOver, onDragEnter, onDragLeave, onDrop, errorMessage, ...restProps } = props;
    const [isDragActive, setIsDragActive] = reactUtils.useBoolean(false);
    const dropZoneRef = React__namespace.useRef(null);
    const hiddenFileInput = React__namespace.useRef(null);
    const cx = reactUtils.useStyles(Uploader_module.default);
    const uploaderStyles = cx('upload-container', {
        disabled,
        error: errorMessage,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'drop-zone-active': isDragActive,
    });
    const uploadCaptionClassNames = cx('upload-caption', {
        disabled,
    });
    const uploadFiles = React__namespace.useCallback((files) => {
        if (!files.length)
            return;
        const filesToUpload = [];
        const count = multiple ? files.length : 1;
        const innerAccept = accept?.replace(/\s/g, '').split(',');
        for (let i = 0; i < count; i++) {
            const file = files.item(i);
            if (!file) {
                continue;
            }
            if (innerAccept) {
                innerAccept.some((accept) => file?.type.match(accept)) && filesToUpload.push(file);
            }
            else {
                filesToUpload.push(file);
            }
        }
        onFilesUpload?.(filesToUpload);
    }, [accept, multiple, onFilesUpload]);
    const handleDragEnter = React__namespace.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragEnter?.(event);
        if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
            setIsDragActive.on();
        }
    }, [onDragEnter, setIsDragActive]);
    const handleDragLeave = React__namespace.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragLeave?.(event);
        setIsDragActive.off();
    }, [onDragLeave, setIsDragActive]);
    const handleDragOver = React__namespace.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onDragOver?.(event);
        !isDragActive && setIsDragActive.on();
    }, [isDragActive, onDragOver, setIsDragActive]);
    const handleDrop = React__namespace.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive.off();
        onDrop?.(event);
        if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
            uploadFiles(event.dataTransfer.files);
            event.dataTransfer.clearData();
        }
    }, [onDrop, setIsDragActive, uploadFiles]);
    const handleClick = React__namespace.useCallback(() => !disabled && hiddenFileInput.current && hiddenFileInput.current.click(), [disabled, hiddenFileInput]);
    const handleChange = React__namespace.useCallback((event) => {
        const fileList = event.target.files;
        onChange?.(event);
        if (!fileList)
            return;
        uploadFiles(fileList);
    }, [uploadFiles, onChange]);
    const uploadedFiles = React__namespace.useMemo(() => {
        if (!React__namespace.Children.count(children))
            return;
        return (React__namespace.createElement(Space.Space, { size: 8, direction: "vertical" }, React__namespace.Children.map(children, (child) => {
            if (child == null)
                return;
            return React__namespace.cloneElement(child, { ...child.props });
        })));
    }, [children]);
    React__namespace.useEffect(() => {
        const ref = dropZoneRef?.current;
        if (ref && !disabled) {
            ref.addEventListener('dragenter', handleDragEnter);
            ref.addEventListener('dragleave', handleDragLeave);
            ref.addEventListener('dragover', handleDragOver);
            ref.addEventListener('drop', handleDrop);
        }
        return () => {
            ref?.removeEventListener('dragenter', handleDragEnter);
            ref?.removeEventListener('dragleave', handleDragLeave);
            ref?.removeEventListener('dragover', handleDragOver);
            ref?.removeEventListener('drop', handleDrop);
        };
    }, [disabled, dropZoneRef, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);
    return (React__namespace.createElement(Space.Space, { "data-qa": "Uploader", className: cx(className), size: 16, direction: "vertical" },
        title && React__namespace.createElement("div", { className: cx('title') }, title),
        description && React__namespace.createElement(Alert.Alert, { color: "orange" }, description),
        React__namespace.createElement(Space.Space, { direction: "vertical", size: 8 },
            React__namespace.createElement("div", { "data-qa": "DropZone", ref: dropZoneRef, className: uploaderStyles, onClick: handleClick },
                React__namespace.createElement("input", { ref: reactUtils.useMergedRef(hiddenFileInput, ref), type: "file", className: cx('upload-input'), accept: accept, multiple: multiple, disabled: disabled, onChange: handleChange, ...restProps }),
                (multiple || !React__namespace.Children.count(children)) && (React__namespace.createElement(Space.Space, { size: 12, align: "center" },
                    dragZonePreview && React__namespace.createElement("img", { className: cx('uploader-preview'), src: dragZonePreview, alt: "preview" }),
                    React__namespace.createElement(Text.Text, { className: uploadCaptionClassNames }, dragZoneDescription),
                    React__namespace.createElement(Button.Button, { type: "button", disabled: disabled, className: cx('upload-button') },
                        React__namespace.createElement(Icon.Icon, { icon: React__namespace.createElement(reactIcons.Clip, null) })))),
                uploadedFiles),
            errorMessage && (React__namespace.createElement(Text.Text, { size: 12, className: "h-color-R100" }, errorMessage)))));
});
Uploader.defaultProps = {
    dragZoneDescription: 'Переместите сюда файлы или загрузите с помощью кнопки',
    buttonCaption: 'Загрузить',
};
Uploader.displayName = 'Uploader';

exports.Uploader = Uploader;
//# sourceMappingURL=Uploader.js.map
